<template>
  <div class="iDalgo-Login">
    <!-- Description -->
    <section class="iDalgo-Login-Description" :style="loginDescriptionStyle">
      <h2 class="iDalgo-Login-Description-Logo">
        <a href="https://www.idalgo.fr/">
          <svgicon name="idalgo" />
          iDalgo
        </a>
      </h2>
      <h2 class="iDalgo-Login-Description-Title" v-html="$t(`products.${product}.description.title`)" />
      <p class="iDalgo-Login-Description-Text">{{ $t(`products.${product}.description.text`) }}</p>
    </section>

    <!-- Main -->
    <section class="iDalgo-Login-Main">
      <!-- Error message -->
      <transition name="slideup-fade">
        <div class="iDalgo-Login-Error" v-if="error">
          {{ error }}
          <i class="el-icon-close" @click="resetError" />
        </div>
      </transition>
      <!-- Locale selection -->
      <ida-dropdown uppercase type="menu" position="right">
        {{ currentLanguage }} <i :class="`ida-flag-${currentLanguage}`" />
        <template slot="dropdown">
          <ida-dropdown-item @select="handleLocaleSelection" v-for="(lang, key) in availableLanguages" :key="`lang-${key}`" :value="lang" uppercase>
            {{ lang }} <i :class="`ida-flag-${lang}`" />
          </ida-dropdown-item>
        </template>
      </ida-dropdown>

      <!-- Login form -->
      <iDalgo-login-form-SSO v-if="showSSOForm" />
      <iDalgo-login-form-classical v-else />
    </section>
  </div>
</template>

<script>
import '@/components/icons'
import { mapGetters } from 'vuex'
import styles from '@/assets/styles/styles.scss'
import Cookies from 'js-cookie'
import iDalgoLoginFormClassical from './iDalgo-Login-Form-Classical'
import iDalgoLoginFormSSO from './iDalgo-Login-Form-SSO'

export default {
  name: 'iDalgo-Login',
  components: { iDalgoLoginFormClassical, iDalgoLoginFormSSO },
  props: {
    showSSOForm: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      product: 'product/product',
      error: 'auth/error'
    }),
    currentLanguage () {
      return this.$i18n.locale
    },
    availableLanguages () {
      return Object.keys(this.$i18n.messages).filter(lang => lang !== this.currentLanguage && lang !== 'iDebug')
    },
    loginDescriptionStyle () {
      return {
        backgroundColor: this.mainColor
      }
    },
    mainColor () {
      return styles[`${this.product}Color`]
    }
  },
  methods: {
    handleLocaleSelection (locale) {
      this.$i18n.locale = locale.toLowerCase()
      Cookies.set('locale', locale, { expires: 20000, domain: process.env.DOMAIN })
    },
    resetError () {
      this.$store.commit('auth/RESET_ERROR')
    }
  }
}
</script>

<style lang="scss">
.iDalgo-Login {
  display: flex;
  height: 100vh;

  &-Description {
    background-color: lightgray;
    width: 40%;
    padding-top: 10vh;
    box-sizing: border-box;

    & > * {
      width: 60%;
      max-width: 500px;
      margin: 0 auto;
    }

    &-Logo {
      a {
        display: flex;
        align-items: center;
        color: $white;
        font-size: 20px;
        font-weight: bold;

        & > svg {
          width: 26px;
          height: 26px;
          margin-right: 7px;
        }
      }
      margin-bottom: 100px;
    }

    &-Title {
      font-size: 32px;
      color: $white;
      font-family: $--font-bold;
      line-height: 43px;
      margin-bottom: 40px;
    }

    &-Text {
      height: 50vh;
      font-size: 18px;
      color: $white;
      margin-top: 50px;
      line-height: 30px;
    }
  }

  &-Main {
    width: 60%;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    position: relative;

    & > .ida-dropdown {
      position: absolute;
      width: 100px;
      background: transparent;
      top: 60px;
      right: 60px;

      .ida-dropdown-title {
        display: flex;
        justify-content: center;
        position: relative;

        &::after {
          content: '\EA10';
          display: block;
          width: 6px;
          color: '#2A3243';
          font-family: 'ida-icons' !important;
          position: absolute;
          right: 12px;
          top: 17px;
          transform: rotate(-90deg);
          transition: transform 0.2S;
        }
      }

      i {
        height: 13px;
        margin-left: 6px;
        box-sizing: border-box;
        display: inline-block;
      }

      &--isOpen {
        .ida-dropdown-title {
          &::after {
            transform: rotate(0deg);
          }
        }
      }
    }

    &-Form-Container {
      width: 54%;

      .iDalgo-Login-Main-Title {
        font-size: 26px;
        font-weight: bold;
        display: flex;
        line-height: 56px;
        margin-bottom: 30px;

        &-Icon {
          display: block;
          width: 56px;
          min-width: 56px;
          height: 56px;
          border-radius: 1000px;
          margin-right: 20px;

          & > i {
            color: $white;
            font-size: 28px;
            font-weight: 100;
            align-items: center;
            justify-content: center;
            display: flex;
            width: 100%;
            height: 100%;
          }
        }
      }

      .iDalgo-Login-Main-Form {
        .el-form-item__label {
          color: #A2A2A2;
          font-weight: bold;
          text-transform: uppercase;
          margin-top: 20px;

          &::before {
            display: none;
          }
        }

        .el-input {
          margin-top: 15px;
          width: 100%;

          &__inner {
            padding: 0 80px 0 37px;
            height: 48px;

            &:-webkit-autofill,
            &:-webkit-autofill:hover,
            &:-webkit-autofill:active,
            &:-webkit-autofill:focus {
              background-color: #F6F7F9 !important;
              box-shadow: 0 0 0 1000px #F6F7F9 inset !important;
              -webkit-box-shadow: 0 0 0 1000px #F6F7F9 inset !important;
              -webkit-text-fill-color: black !important;
            }

            &::placeholder {
              color: #BFBFBF;
              font-size: 16px;
            }
          }
        }

        .el-form-item__error {
          color: #FF1A1A;
          font-size: 14px;
          margin-top: 6px;
        }

        .el-input__inner {
          background-color: #F6F7F9 !important;
        }

        &-ID {
          &-Input {
            font-size: 16px;
          }
        }

        &-Password {
          position: relative;

          &-Input {
            .el-input__inner {
              font-size: 15px;
              padding-bottom: 4px;

              &[type="password"] {
                letter-spacing: 3.5px;
              }

              &:-webkit-autofill,
              &:-webkit-autofill:hover,
              &:-webkit-autofill:active,
              &:-webkit-autofill:focus {
                padding-top: 4px;
              }

              &::placeholder {
                font-size: 11px;
                letter-spacing: 2px;
              }
            }

            &.filled {
              .el-input__inner {
                padding-top: 4px;
              }
            }
          }

          &-Eye {
            position: absolute;
            bottom: 7px;
            right: 30px;
            cursor: pointer;
            padding: 10px;
          }
        }

        &-Submit {
          margin-top: 60px;

          .el-form-item__content {
            display: flex;
            flex-direction: column;
          }

          .ida-button {
            width: 320px;
            margin: auto;
            justify-content: center;
          }

          hr {
            width: 100%;
            margin: 35px 0;
          }
        }
      }

      .ida-button {
        padding: 0 60px !important;
        text-transform: uppercase;
        display: flex;
        align-items: center;

        .ida-button--span {
          display: flex;
          align-items: center;

          > *:not(:last-child) {
            margin-right: 8px;
          }

          svg {
            margin-top: -2px;
          }
        }
      }
    }

    &-Redirect {
      position: absolute;
      top: 56px;
      left: 60px;
    }
  }

  &-Error {
    position: absolute;
    top: 0;
    right: 0;
    padding: 30px 15%;
    color: #FF1A1A;
    background-color: lighten(#FF1A1A, 40%);
    z-index: 4000;
    width: 100%;
    box-sizing: border-box;

    i {
      font-size: 20px;
      position: absolute;
      top: 20px;
      right: 20px;
      cursor: pointer;
    }
  }
}

@media screen and (max-width: 768px) {
  .iDalgo-Login {
    &-Description {
      display: none;
    }

    &-Main {
      width: 100%;

      &-Form-Container {
        width: 80%;
      }

      &-Redirect {
        left: 50px;
      }
    }
  }
}

.ida-dropdown-menu {
  background-color: #fff;
  .ida-dropdown-shadow {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 0 6px rgba(0, 0, 0, 0.02);
    background-color: #fff;
  }

  .ida-dropdown-items {
    background-color: #fff;
    .ida-dropdown-item {
      background-color: #fff;
      display: flex;
      justify-content: center;

      &:hover {
        background-color: #fcfcfc;
      }
    }
    i {
      height: 13px;
      margin-left: 6px;
      box-sizing: border-box;
      display: inline-block;
    }
  }
}

.slideup-fade-enter-active, .slideup-fade-leave-active {
  transition: all 0.3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slideup-fade-enter, .slideup-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/iDalgo-Login.vue